import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Typography,
} from '@mui/material';
import { JSX } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EvaluationFormValues } from '../../../../library/models/evaluation';
import ResourcesIds from '../../../../resources/ResourcesIds';
import {
    consentCheckboxStyles,
    gdprParagraphStyles,
    sectionTitleStyles,
} from '../evaluation.styles';

interface GdprStepProps {
    control: Control<EvaluationFormValues>;
    errors: FieldErrors<EvaluationFormValues>;
}

/**
 * The data-processing agreement, shown in full.
 *
 * <p>It is the last step rather than the first because it is what makes storing
 * everything on the two before it lawful -- and the copy shown here has to stay word for
 * word what the generated PDF says. The authoritative wording lives in the backend
 * template (documents/evaluation.xhtml); these keys mirror it.
 *
 * <p>It is also the one step that is not always there. The agreement is given once per
 * patient per clinic, so a returning patient is not shown it and the wizard is two steps
 * long -- see `Evaluation`, which asks the API whether there is one on file. Whether it
 * appears is never decided from the route: a patient an admin invited and nobody has
 * evaluated reaches the follow-up URL having agreed to nothing.
 *
 * <p>The signature is deliberately *not* here. It signs the treatment consent as well,
 * which is given at every visit, so it lives on the step before this one -- see
 * `ConsentStep`.
 */
export const GdprStep = (props: GdprStepProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { control, errors } = props;

    return (
        <Box>
            <Typography variant="h6" sx={sectionTitleStyles}>
                {translate(ResourcesIds.EVALUATION_GDPR_TITLE)}
            </Typography>

            <Typography sx={gdprParagraphStyles}>
                {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_1)}
            </Typography>
            <Typography sx={gdprParagraphStyles}>
                {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_2)}
            </Typography>
            <Typography sx={gdprParagraphStyles}>
                {translate(ResourcesIds.EVALUATION_GDPR_PARAGRAPH_3)}
            </Typography>

            <FormControl error={!!errors.gdprConsentGiven} sx={consentCheckboxStyles}>
                <Controller
                    name="gdprConsentGiven"
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value} />}
                            label={translate(ResourcesIds.EVALUATION_GDPR_CONSENT_LABEL)}
                        />
                    )}
                />
                {errors.gdprConsentGiven && (
                    <FormHelperText>{errors.gdprConsentGiven.message}</FormHelperText>
                )}
            </FormControl>
        </Box>
    );
};
